import { cloneElement, forwardRef, isValidElement, type HTMLAttributes, type ReactElement, type ReactNode, useId } from "react";
import { cn } from "@/lib/cn";
import { Label } from "../Label";

export type FormFieldProps = HTMLAttributes<HTMLDivElement> & {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  errorText?: ReactNode;
  disabled?: boolean;
  children: ReactElement;
};

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  ({ className, label, required, helperText, errorText, disabled, children, ...props }, ref) => {
    const generatedId = useId();
    const controlId = children.props.id ?? generatedId;
    const helperId = helperText ? `${controlId}-helper` : undefined;
    const errorId = errorText ? `${controlId}-error` : undefined;
    const describedBy = [children.props["aria-describedby"], helperId, errorId].filter(Boolean).join(" ") || undefined;

    const control = isValidElement(children)
      ? cloneElement(children, {
          id: controlId,
          disabled: children.props.disabled ?? disabled,
          "aria-describedby": describedBy,
          "aria-invalid": children.props["aria-invalid"] ?? Boolean(errorText)
        })
      : children;

    return (
      <div ref={ref} className={cn("bfrs-space-y-1.5", className)} {...props}>
        {label && (
          <Label className="bfrs-block" htmlFor={controlId} required={required} disabled={disabled}>
            {label}
          </Label>
        )}
        {control}
        {errorText ? (
          <p id={errorId} className="bfrs-text-sm bfrs-leading-5 bfrs-text-danger">
            {errorText}
          </p>
        ) : helperText ? (
          <p id={helperId} className="bfrs-text-sm bfrs-leading-5 bfrs-text-text-muted">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);

FormField.displayName = "FormField";
